'use client'

import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { Tooltip } from '@qr-front/ui/components/Tooltip'
import { useBreakpoint } from '@qr-front/ui/hooks'
import { cn } from '@qr-front/ui/utils'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { useRef } from 'react'

import { TSvgIcon } from '@/types'

import { captureBuilderEvent } from '../../builderAnalytics'

export const qrStyleOptionCls = cn(
	'relative flex size-[76px] shrink-0 items-center justify-center rounded-10 ring-1 ring-inset ring-border-grey transition focus-visible:outline-none data-[state=checked]:ring-2 data-[state=checked]:ring-blue hover:ring-2',
)

const QrStyleItem = <T extends string | undefined>({
	value,
	Preview,
	color,
	backgroundColor,
	tooltip,
}: {
	value: T
	Preview: TSvgIcon
	color?: string
	backgroundColor?: string
	tooltip?: string
}) => {
	const buttonRef = useRef<HTMLButtonElement>(null)
	const isTablet = useBreakpoint('tablet')

	return (
		<>
			{isTablet && <Tooltip anchorRef={buttonRef}>{tooltip} </Tooltip>}
			<RadioGroup.Item
				ref={buttonRef}
				key={value || 'none'}
				value={value === undefined ? 'none' : value}
				className={qrStyleOptionCls}
				style={{ color: color || 'black' }}
			>
				{value ? (
					<div
						className="absolute inset-2 -z-[1] rounded-8 transition-colors"
						style={{ backgroundColor }}
					/>
				) : null}
				<Preview key={value} />
			</RadioGroup.Item>
		</>
	)
}

export const QrStyleField = <T extends string | undefined>({
	label,
	value,
	setValue,
	optionList,
	color,
	backgroundColor,
	className,
	designElement,
}: {
	label?: string
	value: T
	setValue: (value: T) => void
	optionList: { value: T; Preview: TSvgIcon; tooltip?: string }[]
	color?: string
	backgroundColor?: string
	className?: string
	// MYQRCODE-10778 AC#13: 'pattern' | 'corners' — which registry design_element
	// this instance represents; the shared component can't infer it.
	designElement: 'pattern' | 'corners'
}) => (
	<div className={className}>
		{label ? (
			<label className="type-body-medium mb-2 block font-semibold">
				{label}
			</label>
		) : null}
		<SmartScrollContainer
			onFirstScroll={() =>
				captureBuilderEvent('builder_scroll_options', {
					design_element: designElement,
				})
			}
		>
			<RadioGroup.Root
				value={value}
				onValueChange={(newValue) => {
					captureBuilderEvent('builder_select_design', {
						design_scope: 'qr_code',
						design_element: designElement,
						design_value: newValue,
						option_position: optionList.findIndex(
							(option) => option.value === newValue,
						),
						section: 'wizard_content',
					})
					setValue(newValue as T)
				}}
				className="flex gap-4"
			>
				{optionList.map(({ value, Preview, tooltip }) => (
					<QrStyleItem
						key={value || 'none'}
						value={value}
						Preview={Preview}
						color={color}
						backgroundColor={backgroundColor}
						tooltip={tooltip}
					/>
				))}
			</RadioGroup.Root>
		</SmartScrollContainer>
	</div>
)
